const NETWORK_RETRY_LIMIT = 1;
const NETWORK_RETRY_DELAY_MS = 1000;

export interface IPresignedPostTarget {
  uploadUrl: string;
  fields: Record<string, string>;
  fileKey: string;
}

export function uploadFileToPresignedPost(
  target: Pick<IPresignedPostTarget, 'uploadUrl' | 'fields'>,
  file: File,
  onProgress: (percent: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  const attemptUpload = (): Promise<void> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open('POST', target.uploadUrl);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        reject(
          Object.assign(new Error('Upload network error'), {
            isNetworkError: true,
          }),
        );
      };

      xhr.onabort = () => {
        reject(
          Object.assign(new Error('Upload aborted'), {
            isAbortError: true,
          }),
        );
      };

      if (signal) {
        signal.addEventListener('abort', () => xhr.abort());
      }

      const formData = new FormData();
      Object.entries(target.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      xhr.send(formData);
    });

  const runWithRetry = async (attempt: number): Promise<void> => {
    try {
      await attemptUpload();
    } catch (err) {
      const networkError = (err as { isNetworkError?: boolean })
        ?.isNetworkError;
      if (networkError && attempt < NETWORK_RETRY_LIMIT && !signal?.aborted) {
        await new Promise((resolveDelay) => {
          setTimeout(resolveDelay, NETWORK_RETRY_DELAY_MS);
        });
        return runWithRetry(attempt + 1);
      }
      throw err;
    }
    return undefined;
  };

  return runWithRetry(0);
}
